import { Component, ReactNode } from 'react';

import { ErrorPage } from '~templates';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * ErrorBoundary component that catches JavaScript errors in child component tree.
 * It displays an error page when errors occur.
 *
 * Note: Error boundaries must be class components - React does not provide
 * hooks equivalents for getDerivedStateFromError and componentDidCatch.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 *
 * For async errors (API calls), use the useErrorHandler hook to propagate
 * errors to the boundary.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  render(): ReactNode {
    const { children } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return <ErrorPage />;
    }

    return children;
  }
}

export default ErrorBoundary;
